import React, { useState } from 'react';
import { Box, Stack, Alert, Snackbar, CircularProgress } from '@mui/material';
import { Tabs, Tab } from '@mui/material';
import { EventColumns } from './components/EventColumns';
import { EventForm } from './components/EventForm';
import { EventDialog } from './components/EventDialog';
import { EventToolbar } from './components/EventToolbar';
import { SchoolEvents } from './components/SchoolEvents';
import { Event } from '@/types/events';
import { useEvents } from './hooks/useEvents';

export const EventManagement: React.FC = () => {
  const { events, isLoading, error, addEvent, updateEvent, deleteEvent } = useEvents();
  const [activeTab, setActiveTab] = useState(0);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEventDialogOpen, setIsEventDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleAddEvent = () => {
    setSelectedEvent(null);
    setIsFormOpen(true);
  };

  const handleEditEvent = (event: Event) => {
    setSelectedEvent(event);
    setIsFormOpen(true);
  };

  const handleViewEvent = (event: Event) => {
    setSelectedEvent(event);
    setIsEventDialogOpen(true);
  };

  const handleDeleteEvent = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      await deleteEvent(id);
    }
  };

  const handleSaveEvent = async (eventData: Partial<Event>) => {
    if (selectedEvent) {
      await updateEvent({ ...selectedEvent, ...eventData } as Event);
    } else {
      await addEvent(eventData);
    }
    setIsFormOpen(false);
  };

  const handleUpdateEvent = async (updatedEvent: Event) => {
    await updateEvent(updatedEvent);
    setSelectedEvent(updatedEvent);
  };

  const filteredEvents = events.filter(event => 
    event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    event.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3} sx={{ p: 2, height: '100%' }}>
      <Box>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label="Events" />
          <Tab label="Event Catalog" />
        </Tabs>

        {activeTab === 0 && (
          <EventToolbar
            onAddEvent={handleAddEvent}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
          />
        )}
      </Box>

      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        {activeTab === 0 ? (
          <EventColumns 
            onEdit={handleEditEvent}
            onDelete={handleDeleteEvent}
            onView={handleViewEvent}
          />
        ) : (
          <SchoolEvents />
        )}
      </Box>

      <EventForm
        open={isFormOpen}
        event={selectedEvent}
        onClose={() => setIsFormOpen(false)}
        onSave={handleSaveEvent}
      />

      <EventDialog
        open={isEventDialogOpen}
        event={selectedEvent}
        onClose={() => setIsEventDialogOpen(false)}
        onUpdate={handleUpdateEvent}
      />

      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={() => setSearchQuery('')}
      >
        <Alert severity="error" onClose={() => setSearchQuery('')}>
          {error}
        </Alert>
      </Snackbar>
    </Stack>
  );
};